import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Parse a Catho search URL and surface the geographic filters detected. */
const CathoUrlPreview = ({ url }) => {
  const parsed = (() => {
    try {
      const u = new URL(url);
      if (!u.hostname.includes("catho.com.br")) {
        return { ok: false, reason: "URL precisa ser do domínio catho.com.br" };
      }
      const params = u.searchParams;
      const countryId = params.get("pais_id") || "";
      const stateIds = [];
      const regionIds = [];
      const cityIds = [];
      params.forEach((v, k) => {
        if (k.startsWith("estado_id")) stateIds.push(v);
        if (k.startsWith("regiaoId")) regionIds.push(v);
        if (k.startsWith("cidade_id")) cityIds.push(v);
      });
      return {
        ok: true,
        countryId,
        stateIds,
        regionIds,
        cityIds,
      };
    } catch {
      return { ok: false, reason: "URL inválida" };
    }
  })();

  if (!parsed.ok) {
    return (
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{parsed.reason}</span>
      </div>
    );
  }

  const totalFilters =
    (parsed.countryId ? 1 : 0) +
    parsed.stateIds.length +
    parsed.regionIds.length +
    parsed.cityIds.length;

  return (
    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs">
      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
      <div className="flex-1">
        <div className="font-semibold mb-0.5">
          {totalFilters} filtro(s) geográfico(s) detectado(s)
        </div>
        <div className="text-emerald-700 font-mono text-[10px] flex flex-wrap gap-x-3 gap-y-0.5">
          {parsed.countryId && <span>país: {parsed.countryId}</span>}
          {parsed.stateIds.length > 0 && (
            <span>estado(s): {parsed.stateIds.join(", ")}</span>
          )}
          {parsed.regionIds.length > 0 && (
            <span>região(ões): {parsed.regionIds.join(", ")}</span>
          )}
          {parsed.cityIds.length > 0 && (
            <span>cidade(s): {parsed.cityIds.join(", ")}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const TagInput = ({ values, onChange, placeholder, testId }) => {
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (values.includes(v)) {
      setInput("");
      return;
    }
    onChange([...values, v]);
    setInput("");
  };

  const remove = (idx) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex gap-2">
        <Input
          data-testid={`${testId}-input`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <Button
          type="button"
          data-testid={`${testId}-add`}
          onClick={add}
          variant="outline"
          className="shrink-0"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {values.map((v, i) => (
            <span
              key={`${v}-${i}`}
              data-testid={`${testId}-tag-${i}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200"
            >
              {v}
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`${testId}-remove-${i}`}
                className="hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const JobFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [assessmentCatalog, setAssessmentCatalog] = useState([]);

  const [form, setForm] = useState({
    title: "",
    department: "",
    department_id: "",
    organization_id: "",
    description: "",
    requirements: [],
    skills: [],
    experience_years: 0,
    location: "",
    employment_type: "CLT",
    salary_range: "",
    catho_search_url: "",
    email_template_subject: "",
    email_template_body: "",
    rejection_email_subject: "",
    rejection_email_body: "",
    whatsapp_template: "",
    manager_id: "",
    assessment_weight: 0,
    required_assessments: [],
  });

  useDocumentTitle(
    isEdit ? (form.title ? `Editar — ${form.title}` : "Editar vaga") : "Nova vaga",
  );

  // Fetch managers, organizations and departments once
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/jobs/managers/available`).catch(() => ({ data: [] })),
      axios.get(`${API}/registry/organizations`).catch(() => ({ data: [] })),
      axios.get(`${API}/registry/departments`).catch(() => ({ data: [] })),
      axios.get(`${API}/integrations/gridprofile/catalog`).catch(() => ({ data: [] })),
    ]).then(([mgrs, orgs, deps, cat]) => {
      setManagers(mgrs.data || []);
      setOrganizations(orgs.data || []);
      setDepartments(deps.data || []);
      setAssessmentCatalog(cat.data || []);
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`);
        setForm({
          title: res.data.title || "",
          department: res.data.department || "",
          department_id: res.data.department_id || "",
          organization_id: res.data.organization_id || "",
          description: res.data.description || "",
          requirements: res.data.requirements || [],
          skills: res.data.skills || [],
          experience_years: res.data.experience_years || 0,
          location: res.data.location || "",
          employment_type: res.data.employment_type || "CLT",
          salary_range: res.data.salary_range || "",
          catho_search_url: res.data.catho_search_url || "",
          email_template_subject: res.data.email_template_subject || "",
          email_template_body: res.data.email_template_body || "",
          rejection_email_subject: res.data.rejection_email_subject || "",
          rejection_email_body: res.data.rejection_email_body || "",
          whatsapp_template: res.data.whatsapp_template || "",
          manager_id: res.data.manager_id || "",
          assessment_weight: res.data.assessment_weight ?? 0,
          required_assessments: res.data.required_assessments || [],
        });
      } catch {
        toast.error("Não foi possível carregar a vaga");
        navigate("/vagas");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit, navigate]);

  // If organization changes, clear department_id if the chosen department
  // doesn't belong to the new org.
  useEffect(() => {
    if (!form.department_id) return;
    const dep = departments.find((d) => d.id === form.department_id);
    if (dep && dep.organization_id && form.organization_id && dep.organization_id !== form.organization_id) {
      setForm((f) => ({ ...f, department_id: "" }));
    }
  }, [form.organization_id, departments, form.department_id]);

  const filteredDepartments = form.organization_id
    ? departments.filter((d) => !d.organization_id || d.organization_id === form.organization_id)
    : departments;

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) {
      toast.error("Informe o título da vaga");
      return;
    }
    if (form.description.trim().length < 10) {
      toast.error("Descreva a vaga com mais detalhes (mínimo 10 caracteres)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        experience_years: Number(form.experience_years) || 0,
      };
      if (isEdit) {
        await axios.put(`${API}/jobs/${id}`, payload);
        toast.success("Vaga atualizada");
        navigate(`/vagas/${id}`);
      } else {
        const res = await axios.post(`${API}/jobs`, payload);
        toast.success("Vaga criada");
        navigate(`/vagas/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar vaga");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Carregando...</div>;
  }

  return (
    <div data-testid="job-form-page" className="max-w-3xl space-y-6">
      <Link
        to={isEdit ? `/vagas/${id}` : "/vagas"}
        data-testid="back-link"
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
          {isEdit ? "Editar vaga" : "Nova vaga"}
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
          {isEdit ? "Editar vaga" : "Criar nova vaga"}
        </h1>
        <p className="text-sm text-slate-600 mt-2">
          Descreva detalhadamente as características desejadas — quanto mais informações,
          mais precisa será a análise da IA.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="title">Título da vaga *</Label>
            <Input
              id="title"
              data-testid="job-title-input"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Ex: Desenvolvedor(a) Full Stack Pleno"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="organization">Empresa</Label>
              <Select
                value={form.organization_id || "__none__"}
                onValueChange={(v) => update("organization_id", v === "__none__" ? "" : v)}
              >
                <SelectTrigger id="organization" data-testid="job-organization-select">
                  <SelectValue placeholder={organizations.length ? "Selecione a empresa" : "Nenhuma cadastrada"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhuma —</SelectItem>
                  {organizations.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}>
                      {o.trade_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {organizations.length === 0 && (
                <p className="text-[11px] text-amber-700">
                  Nenhuma empresa cadastrada.{" "}
                  <Link to="/cadastros" className="underline">Cadastrar agora →</Link>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Departamento</Label>
              <Select
                value={form.department_id || "__none__"}
                onValueChange={(v) => update("department_id", v === "__none__" ? "" : v)}
              >
                <SelectTrigger id="department" data-testid="job-department-select">
                  <SelectValue placeholder={filteredDepartments.length ? "Selecione o departamento" : "Nenhum cadastrado"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhum —</SelectItem>
                  {filteredDepartments.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`dep-option-${d.id}`}>
                      {d.name}
                      {d.organization_name ? ` · ${d.organization_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filteredDepartments.length === 0 && (
                <p className="text-[11px] text-amber-700">
                  Nenhum departamento disponível.{" "}
                  <Link to="/cadastros" className="underline">Cadastrar agora →</Link>
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2 max-w-md">
            <Label htmlFor="location">Localização</Label>
            <Input
              id="location"
              data-testid="job-location-input"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="Ex: São Paulo - SP / Remoto"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employment">Tipo de contratação</Label>
              <Select
                value={form.employment_type}
                onValueChange={(v) => update("employment_type", v)}
              >
                <SelectTrigger id="employment" data-testid="job-employment-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                  <SelectItem value="Estágio">Estágio</SelectItem>
                  <SelectItem value="Freelance">Freelance</SelectItem>
                  <SelectItem value="Temporário">Temporário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience">Anos de experiência mínimos</Label>
              <Input
                id="experience"
                data-testid="job-experience-input"
                type="number"
                min={0}
                max={50}
                value={form.experience_years}
                onChange={(e) => update("experience_years", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="salary">Faixa salarial</Label>
              <Input
                id="salary"
                data-testid="job-salary-input"
                value={form.salary_range}
                onChange={(e) => update("salary_range", e.target.value)}
                placeholder="Ex: R$ 8.000 - R$ 12.000"
              />
            </div>
          </div>

          {/* Gestor responsável */}
          <div className="space-y-2 max-w-md">
            <Label htmlFor="manager">Gestor responsável pela entrevista</Label>
            <Select
              value={form.manager_id || "__none__"}
              onValueChange={(v) => update("manager_id", v === "__none__" ? "" : v)}
            >
              <SelectTrigger id="manager" data-testid="job-manager-select">
                <SelectValue placeholder="Selecione um gestor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nenhum —</SelectItem>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`manager-option-${m.id}`}>
                    {m.name}{m.email ? ` · ${m.email}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Apenas usuários com perfil <strong>Gestor</strong> aparecem aqui. O RH poderá enviar
              candidatos diretamente para este gestor após a triagem.
              {managers.length === 0 && (
                <span className="block text-amber-700 mt-1">
                  Nenhum gestor cadastrado ainda — crie em Configurações → Acessos → Usuários.
                </span>
              )}
            </p>
          </div>

          {/* Peso dos testes do GridProfile no ranking */}
          <div className="space-y-2 max-w-md">
            <Label htmlFor="assessment-weight" className="flex items-center justify-between">
              <span>Peso dos testes no ranking</span>
              <span
                className="text-violet-700 font-bold tabular-nums"
                data-testid="assessment-weight-value"
              >
                {Math.round((form.assessment_weight || 0) * 100)}%
              </span>
            </Label>
            <input
              type="range"
              id="assessment-weight"
              data-testid="assessment-weight-input"
              min={0}
              max={70}
              step={5}
              value={Math.round((form.assessment_weight || 0) * 100)}
              onChange={(e) =>
                update("assessment_weight", Number(e.target.value) / 100)
              }
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <p className="text-xs text-slate-500">
              Quando &gt; 0%, o ranking final = (score do CV) × (1−peso) + (média dos testes) × peso.
              Candidatos que ainda não fizeram os testes ficam proporcionalmente abaixo até completarem.
              Recomendado: <strong>30%</strong> pra vagas que dependem de perfil comportamental ou técnico.
            </p>
          </div>

          {/* Testes obrigatórios — aplicados automaticamente quando candidato entra em "Em entrevista" */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-base">
              <span>🎯 Testes obrigatórios desta vaga</span>
              {form.required_assessments?.length > 0 && (
                <span
                  data-testid="required-assessments-count"
                  className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-semibold"
                >
                  {form.required_assessments.length} selecionado
                  {form.required_assessments.length > 1 ? "s" : ""}
                </span>
              )}
            </Label>
            <p className="text-xs text-slate-500">
              Quando um candidato é movido para <strong>"Em entrevista"</strong>, a Maya cria automaticamente
              os testes do GridProfile abaixo e envia os links pra ele via WhatsApp (com fallback Email).
              Idempotente — testes já criados não são duplicados.
            </p>
            {assessmentCatalog.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Carregando catálogo do GridProfile…</p>
            ) : (
              <div
                data-testid="required-assessments-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3"
              >
                {assessmentCatalog.map((t) => {
                  const checked = (form.required_assessments || []).includes(t.test_id);
                  return (
                    <label
                      key={t.test_id}
                      data-testid={`required-assessment-${t.test_id}`}
                      className={`flex items-start gap-2.5 p-2.5 rounded-md cursor-pointer border transition-all ${
                        checked
                          ? "bg-violet-50 border-violet-300 shadow-sm"
                          : "bg-white border-slate-200 hover:border-violet-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 w-4 h-4 accent-violet-600 cursor-pointer"
                        checked={checked}
                        onChange={(e) => {
                          const list = form.required_assessments || [];
                          if (e.target.checked) {
                            update("required_assessments", [...list, t.test_id]);
                          } else {
                            update(
                              "required_assessments",
                              list.filter((x) => x !== t.test_id),
                            );
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                          {t.name}
                          {t.duration_minutes ? (
                            <span className="text-[10px] text-slate-500 font-normal">
                              · {t.duration_minutes} min
                            </span>
                          ) : null}
                        </div>
                        {t.description && (
                          <div className="text-xs text-slate-500 leading-snug mt-0.5 line-clamp-2">
                            {t.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição da vaga *</Label>
            <Textarea
              id="description"
              data-testid="job-description-input"
              rows={6}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Descreva responsabilidades, contexto da equipe, projetos e o perfil desejado..."
              required
            />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div>
            <Label className="text-base">Requisitos</Label>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Adicione um requisito por vez (pressione Enter ou clique em +).
            </p>
            <TagInput
              values={form.requirements}
              onChange={(v) => update("requirements", v)}
              placeholder="Ex: Graduação em Ciência da Computação"
              testId="job-requirements"
            />
          </div>

          <div>
            <Label className="text-base">Habilidades desejadas</Label>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Tecnologias, ferramentas e competências (ex: React, Liderança, Inglês fluente).
            </p>
            <TagInput
              values={form.skills}
              onChange={(v) => update("skills", v)}
              placeholder="Ex: React"
              testId="job-skills"
            />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
          <div>
            <Label htmlFor="catho-url" className="text-base">
              URL de busca da Catho
              <span className="text-slate-400 font-normal ml-1.5">(opcional)</span>
            </Label>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              Faça uma busca de currículos na Catho com os filtros desejados (estado, regiões,
              cidades), copie a URL da barra de endereços e cole abaixo. Vamos manter seus
              filtros geográficos e atualizar automaticamente o termo de busca (
              <span className="font-mono">q=</span>) com o título e habilidades desta vaga.
            </p>
          </div>
          <Input
            id="catho-url"
            data-testid="job-catho-url-input"
            value={form.catho_search_url}
            onChange={(e) => update("catho_search_url", e.target.value)}
            placeholder="https://www.catho.com.br/curriculos/busca/?q=...&estado_id[25]=25&cidade_id[97]=97"
            className="font-mono text-xs"
          />
          {form.catho_search_url && (
            <CathoUrlPreview url={form.catho_search_url} />
          )}
        </div>

        {/* Templates de mensagem por vaga */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div>
            <h2 className="heading text-base font-semibold text-slate-900 mb-1">
              Templates de mensagem
            </h2>
            <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">
              Personalize as mensagens enviadas aos candidatos pré-selecionados. Variáveis
              disponíveis:{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{name}"}</code>{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{title}"}</code>{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{location}"}</code>{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{employment_type}"}</code>{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{salary_range}"}</code>{" "}
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{description}"}</code>.
              Deixe em branco para usar o template padrão.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-template-subject">Assunto do e-mail</Label>
            <Input
              id="email-template-subject"
              data-testid="job-email-subject-input"
              value={form.email_template_subject}
              onChange={(e) => update("email_template_subject", e.target.value)}
              placeholder="Você foi pré-selecionado(a) para a vaga: {title}"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-template-body">Corpo do e-mail</Label>
            <Textarea
              id="email-template-body"
              data-testid="job-email-body-input"
              rows={8}
              value={form.email_template_body}
              onChange={(e) => update("email_template_body", e.target.value)}
              placeholder={"Olá {name},\n\nTemos uma boa notícia: você foi pré-selecionado(a) para a vaga {title} em {location}..."}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wa-template">Mensagem WhatsApp</Label>
            <Textarea
              id="wa-template"
              data-testid="job-wa-template-input"
              rows={6}
              value={form.whatsapp_template}
              onChange={(e) => update("whatsapp_template", e.target.value)}
              placeholder={"Olá {name}! 👋\n\nVocê foi pré-selecionado(a) para a vaga *{title}*..."}
            />
          </div>

          <div className="border-t border-slate-200 pt-5 mt-2 space-y-4">
            <div className="bg-rose-50/40 border border-rose-200 rounded-md p-4">
              <h4 className="text-sm font-semibold text-slate-900 mb-1">
                E-mail automático de rejeição
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Quando um candidato for marcado como <strong>Rejeitado</strong>, o
                sistema enviará automaticamente um e-mail gentil <strong>3 dias depois</strong>{" "}
                agradecendo a candidatura. Personalize o template abaixo ou deixe em
                branco para usar o padrão profissional.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rejection-subject">Assunto do e-mail de rejeição</Label>
              <Input
                id="rejection-subject"
                data-testid="job-rejection-subject-input"
                value={form.rejection_email_subject}
                onChange={(e) => update("rejection_email_subject", e.target.value)}
                placeholder="Atualização sobre sua candidatura — {title}"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rejection-body">Corpo do e-mail de rejeição</Label>
              <Textarea
                id="rejection-body"
                data-testid="job-rejection-body-input"
                rows={8}
                value={form.rejection_email_body}
                onChange={(e) => update("rejection_email_body", e.target.value)}
                placeholder={
                  "Olá {name},\n\nAgradecemos seu interesse na vaga {title} e o tempo dedicado ao nosso processo seletivo.\n\nApós uma análise criteriosa, optamos por seguir com outros perfis nesta etapa..."
                }
              />
              <p className="text-xs text-slate-500">
                Variáveis disponíveis:{" "}
                <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{name}"}</code>{" "}
                <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{title}"}</code>{" "}
                <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{location}"}</code>{" "}
                <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{employment_type}"}</code>{" "}
                <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"{recruiter}"}</code>.
                A assinatura usa o nome do recrutador que rejeitou o candidato.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 justify-end">
          <Link
            to={isEdit ? `/vagas/${id}` : "/vagas"}
            className="inline-flex items-center px-4 py-2.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="cancel-job-button"
          >
            Cancelar
          </Link>
          <Button
            type="submit"
            data-testid="submit-job-button"
            disabled={submitting}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white px-5 py-2.5"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Salvar alterações" : "Criar vaga"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default JobFormPage;
